// MODEL-POLICY: Setup › Model policy. The company's model policy for gateway runs (M3-07's
// `company.settings.model_policy`: residency, the allow-list, the default model, and the
// default per task kind), with what it needs to be edited in one read: the gateway's model
// list and regions, the BYO-01 subscription policy (whether people may bring their own AI keys;
// its company flags are written through `PATCH /v1/companies/{co}` like Keys & connections), and
// the per-agent model overrides (agent profiles' `overrides.model` and the DEs' role-to-model
// maps), which are read-only here and changed where they live.
import { z } from "zod";
import { SubscriptionPolicy } from "../connection-rows";
import { AgentProfileOwnerKind, ModelAccessMode } from "../enums";
import { Uuid } from "../primitives";
import { CompanyModelPolicy, ModelAlias, ModelRegion, ModelTaskKind } from "../rows";
import { CompanyParams } from "./common";
import { defineRoute } from "./route";

const co = "/v1/companies/{co}";
const core = { feature: "core", tag: "model-policy" } as const;

/** One model of the gateway's list (`LITELLM_MODELS`, `LITELLM_MODEL_REGIONS`). */
export const GatewayModel = z
  .object({
    alias: ModelAlias,
    /** The regions it is served in; empty when the gateway names none (fails any residency). */
    regions: z.array(ModelRegion),
    /** Routable under the company's policy (allowed and inside its residency). */
    allowed: z.boolean(),
  })
  .meta({ id: "GatewayModel" });

/** A model an agent names for itself, outside the company default (read-only on this page). */
export const ModelPolicyOverride = z
  .object({
    agent_kind: AgentProfileOwnerKind,
    /** The DE's or the crew's membership (the agent's profile link). */
    membership_id: Uuid,
    name: z.string(),
    /** `profile`: the agent profile's `overrides.model`; `role_model_map`: the DE's override. */
    source: z.enum(["profile", "role_model_map"]),
    /** The task kind of a role-to-model map entry; null for a profile override. */
    role: z.string().nullable(),
    model: z.string(),
    /** Routable under the policy; null when it cannot be checked (no gateway list, no allow-list). */
    allowed: z.boolean().nullable(),
  })
  .meta({ id: "ModelPolicyOverride" });

export const ModelPolicyView = z
  .object({
    policy: CompanyModelPolicy,
    gateway: z.object({
      /** False when the gateway lists no models: the policy's models cannot be checked. */
      listed: z.boolean(),
      models: z.array(GatewayModel),
    }),
    /** The task kinds the page offers a default for, then any other kind the policy names. */
    task_kinds: z.array(ModelTaskKind),
    overrides: z.object({
      data: z.array(ModelPolicyOverride),
      /** More overrides exist than the page lists (200). */
      truncated: z.boolean(),
    }),
    /** BYO-01: may people bring their own AI keys here (platform switch AND company flag). */
    byo: SubscriptionPolicy,
  })
  .meta({ id: "ModelPolicyView" });
export type ModelPolicyView = z.infer<typeof ModelPolicyView>;

/**
 * The whole policy, replaced. `null` clears a field (no residency; every gateway model allowed;
 * the gateway's first model as the default); `role_defaults` lists only the kinds with a default.
 */
export const ModelPolicyPut = z
  .strictObject({
    residency: ModelRegion.nullable(),
    allowed_models: z.array(ModelAlias).min(1).max(50).nullable(),
    default_model: ModelAlias.nullable(),
    role_defaults: CompanyModelPolicy.shape.role_defaults.unwrap(),
  })
  .meta({ id: "ModelPolicyPut" });
export type ModelPolicyPut = z.infer<typeof ModelPolicyPut>;

/**
 * MODEL-KEYS-PLANS: Connections › API keys › Model access. `allowed`: the company's plan lets it
 * bring its own keys (operator data); the choice applies only while it does (`effective`). One
 * live company key per provider (`providers`, never the key itself: its last four only).
 */
export const ModelAccessView = z
  .object({
    mode: ModelAccessMode,
    fallback_to_ours: z.boolean(),
    allowed: z.boolean(),
    plan_key: z.string().nullable(),
    /** What model calls use now: `own` only when chosen AND the plan allows it. */
    effective: ModelAccessMode,
    providers: z.array(
      z.object({
        provider: z.string(),
        credential_id: Uuid,
        label: z.string(),
        key_last4: z.string().nullable(),
      }),
    ),
    /** The providers the gateway's models need, for the "missing key" hints. */
    needed: z.array(z.string()),
  })
  .meta({ id: "ModelAccessView" });
export type ModelAccessView = z.infer<typeof ModelAccessView>;

export const ModelAccessPut = z
  .strictObject({ mode: ModelAccessMode, fallback_to_ours: z.boolean() })
  .meta({ id: "ModelAccessPut" });
export type ModelAccessPut = z.infer<typeof ModelAccessPut>;

export const modelPolicyRoutes = {
  "modelAccess.get": defineRoute({
    ...core,
    id: "modelAccess.get",
    method: "get",
    path: `${co}/model-access`,
    summary: "How model calls are paid: our keys (billed) or the company's own, per plan",
    access: "manager",
    params: CompanyParams,
    response: { status: 200, schema: ModelAccessView },
    errors: [],
    emits: [],
  }),
  "modelAccess.put": defineRoute({
    ...core,
    id: "modelAccess.put",
    method: "put",
    path: `${co}/model-access`,
    summary: "Choose our keys (billed) or the company's own keys (only when the plan allows it)",
    access: "manager",
    params: CompanyParams,
    body: ModelAccessPut,
    response: { status: 200, schema: ModelAccessView },
    errors: ["forbidden"],
    emits: ["company.model_access_changed"],
  }),
  "modelPolicy.get": defineRoute({
    ...core,
    id: "modelPolicy.get",
    method: "get",
    path: `${co}/model-policy`,
    summary: "The company's model policy, the gateway's models, BYO keys and agent overrides",
    access: "manager",
    params: CompanyParams,
    response: { status: 200, schema: ModelPolicyView },
    errors: [],
    emits: [],
  }),
  "modelPolicy.put": defineRoute({
    ...core,
    id: "modelPolicy.put",
    method: "put",
    path: `${co}/model-policy`,
    summary: "Set the allowed models, the default and the default per task kind",
    access: "manager",
    params: CompanyParams,
    body: ModelPolicyPut,
    response: { status: 200, schema: ModelPolicyView },
    errors: [],
    emits: ["company.model_policy_changed"],
  }),
} as const;
